import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { staffLeaveAPI } from '../../services/api';
import { leaveStatusLabel } from '../Payroll/plainLabels';
import { PageHeader, Section } from '../Payroll/payrollUi';

const SKIP_LABEL = {
  weekly_off: 'weekly off',
  holiday: 'holiday',
  before_joining: 'before joining',
  after_exit: 'after exit',
};

function skippedText(skipped) {
  if (!skipped?.length) return '';
  return skipped.map((s) => `${s.date} (${SKIP_LABEL[s.reason] || s.reason})`).join(', ');
}

export default function StaffLeavePage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    leaveTemplateLineId: '',
    startDate: '',
    endDate: '',
    daySpan: 'full',
  });

  const mine = useQuery(['staff-leave-me'], async () => {
    const res = await staffLeaveAPI.getMine();
    return res.data.data;
  });

  const applyMut = useMutation((body) => staffLeaveAPI.apply(body), {
    onSuccess: (res) => {
      qc.invalidateQueries(['staff-leave-me']);
      const skipped = res.data.data?.skipped || [];
      const balance = res.data.data?.balance;
      toast.success(
        skipped.length
          ? `Saved. Skipped: ${skippedText(skipped)}`
          : 'Leave request saved and waiting for approval'
      );
      if (balance?.overdrawn) {
        toast('The balance for this leave type is already used up. Extra days stay unpaid.', { icon: '!' });
      }
      setForm((f) => ({ ...f, startDate: '', endDate: '' }));
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not apply'),
  });

  const cancelMut = useMutation((id) => staffLeaveAPI.cancel(id), {
    onSuccess: () => {
      qc.invalidateQueries(['staff-leave-me']);
      toast.success('Leave cancelled');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not cancel'),
  });

  if (mine.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (mine.isError) {
    return <Alert severity="error">{mine.error?.response?.data?.message || 'Could not load leave'}</Alert>;
  }

  const data = mine.data;
  const lines = data?.balances?.lines || [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title="My leave"
        subtitle={`Send a request here, including an earlier day this month. It can be filed until salary for that month is processed. After that, the month stays as it was. This year runs ${data?.balances?.leaveYear?.start || '1 April'} to ${data?.balances?.leaveYear?.end || '31 March'}.`}
      />

      {data?.approverNotDecided && (
        <Alert severity="warning">
          Who approves this role’s leave is not decided yet, so a request cannot be saved.
        </Alert>
      )}
      {data?.blocked && <Alert severity="info">{data.blocked}</Alert>}

      <Section title="Days left" hint="A request that is still waiting is not taken off until it is approved.">
        {lines.length === 0 ? (
          <Typography variant="body2">No leave list is assigned yet. Ask finance to set one on Staff profiles.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {lines.map((line) => (
              <Stack key={line.lineId} direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography>{line.leaveTypeName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {line.used} used{line.waiting ? ` · ${line.waiting} waiting` : ''}
                  </Typography>
                </Box>
                <Typography variant="h6">{line.remaining} left</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Section>

      {data?.canApply && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Ask for leave</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Leave type"
              value={form.leaveTemplateLineId}
              onChange={(e) => setForm({ ...form, leaveTemplateLineId: e.target.value })}
              sx={{ minWidth: 180 }}
            >
              {lines.map((line) => (
                <MenuItem key={line.lineId} value={line.lineId}>{line.leaveTypeName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Day"
              value={form.daySpan}
              onChange={(e) => setForm({ ...form, daySpan: e.target.value })}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="full">Full day</MenuItem>
              <MenuItem value="half">Half day</MenuItem>
            </TextField>
            <TextField
              label="Start"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value, endDate: form.daySpan === 'half' ? e.target.value : form.endDate })}
            />
            <TextField
              label="End"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.daySpan === 'half' ? form.startDate : form.endDate}
              disabled={form.daySpan === 'half'}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
            <Button
              variant="contained"
              disabled={applyMut.isLoading || !form.leaveTemplateLineId || !form.startDate}
              onClick={() =>
                applyMut.mutate({
                  ...form,
                  endDate: form.daySpan === 'half' ? form.startDate : form.endDate,
                })
              }
            >
              Send for approval
            </Button>
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Your requests</Typography>
        {(data?.requests || []).length === 0 ? (
          <Typography variant="body2">No requests yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Full or half</TableCell>
                <TableCell>Days that count</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.requests.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.leaveTypeName}</TableCell>
                  <TableCell>{row.startDate} – {row.endDate}</TableCell>
                  <TableCell>{row.daySpan === 'half' ? 'Half day' : 'Full day'}</TableCell>
                  <TableCell>{(row.countedDates || []).join(', ') || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={leaveStatusLabel(row.status)} />
                    {row.rejectionReason ? ` ${row.rejectionReason}` : ''}
                    {row.skipped?.length ? (
                      <Typography variant="caption" display="block">
                        Skipped: {skippedText(row.skipped)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {(row.status === 'waiting' || row.status === 'approved') && (
                      <Button size="small" onClick={() => cancelMut.mutate(row.id)} disabled={cancelMut.isLoading}>
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
